import React, { useState, useEffect, useCallback } from 'react';
import { PlusIcon, ArrowDownTrayIcon, ArrowsRightLeftIcon, BuildingStorefrontIcon } from '@heroicons/react/24/outline';
import {
    WarehouseFacilitiesTable,
    InventoryDashboard,
    InventoryTransactionsTable,
    RecordIncomingModal,
    RecordOutgoingModal,
    RecordInternalMovementModal,
    FacilityCreationModal,
    FacilityDetailsModal,
    TransactionDetailsModal,
    WarehouseMapDashboard,
    StockWorkflowBoard,
    TraceabilityPanel
} from '../components/warehouse';
import { apiGet, apiPost, apiPut, subscribeToDataInvalidation } from '../utils/api';

const WarehouseView: React.FC = () => {
    // State
    const [facilities, setFacilities] = useState<any[]>([]);
    const [stats, setStats] = useState<any>(null);
    const [transactions, setTransactions] = useState<any[]>([]);
    const [traceability, setTraceability] = useState<any[]>([]);
    const [inventorySnapshot, setInventorySnapshot] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    // Modals
    const [incomingModalOpen, setIncomingModalOpen] = useState(false);
    const [outgoingModalOpen, setOutgoingModalOpen] = useState(false);
    const [internalModalOpen, setInternalModalOpen] = useState(false);
    const [facilityModalOpen, setFacilityModalOpen] = useState(false);
    const [detailsModalOpen, setDetailsModalOpen] = useState(false);
    const [transactionModalOpen, setTransactionModalOpen] = useState(false);
    const [selectedFacility, setSelectedFacility] = useState<any>(null);
    const [selectedTransaction, setSelectedTransaction] = useState<any>(null);
    const [isEditing, setIsEditing] = useState(false);

    // Fetch Data
    const fetchAllData = useCallback(async () => {
        try {
            setLoading(true);
            await Promise.all([
                fetchFacilities(),
                fetchStats(),
                fetchTransactions(),
                fetchTraceability(),
                fetchInventory()
            ]);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchAllData();
    }, [fetchAllData]);

    useEffect(() => {
        const intervalId = setInterval(fetchAllData, 5 * 60 * 1000);
        return () => clearInterval(intervalId);
    }, [fetchAllData]);

    useEffect(() => {
        return subscribeToDataInvalidation(() => {
            fetchAllData();
        });
    }, [fetchAllData]);

    const fetchFacilities = async () => {
        try {
            const data = await apiGet('/api/warehouses/facilities') as any[];
            console.log('Fetched facilities:', data);
            setFacilities(data);
        } catch (error) {
            console.error('Error fetching facilities:', error);
            setFacilities([]);
        }
    };

    const fetchStats = async () => {
        try {
            const data = await apiGet('/api/warehouses/stats') as any;
            setStats(data);
        } catch (error) {
            console.error('Error fetching stats:', error);
            setStats(null);
        }
    };

    const fetchTransactions = async () => {
        try {
            const data = await apiGet('/api/warehouses/transactions?limit=50') as any[];
            setTransactions(data);
        } catch (error) {
            console.error('Error fetching transactions:', error);
            setTransactions([]);
        }
    };

    const fetchTraceability = async () => {
        try {
            const data = await apiGet('/api/warehouses/traceability') as any;
            setTraceability(data?.traceability || []);
        } catch (error) {
            console.error('Error fetching traceability:', error);
            setTraceability([]);
        }
    };

    const fetchInventory = async () => {
        try {
            const data = await apiGet('/api/warehouses/inventory') as any;
            setInventorySnapshot(data?.inventory || []);
        } catch (error) {
            console.error('Error fetching inventory snapshot:', error);
            setInventorySnapshot([]);
        }
    };

    // Handlers
    const handleRecordIncoming = async (data: any) => {
        try {
            console.log('Recording incoming produce:', data);
            const payload = {
                facility_id: Number(data.facilityId),
                crop_type: data.cropType,
                item_type: data.itemType || 'produce',
                quantity_kg: Number(data.quantityKg),
                direction: 'incoming',
                reason: data.reason || 'harvest',
                related_farmer_id: data.farmerId ? Number(data.farmerId) : null,
                related_cohort_id: data.cohortId ? Number(data.cohortId) : null,
                quality_grade: data.qualityGrade || null,
                temperature_at_transaction: data.temperature ? Number(data.temperature) : null,
                humidity_at_transaction: data.humidity ? Number(data.humidity) : null,
                expiry_date: data.expiryDate || null,
                batch_reference: data.batchReference || null,
                source_type: data.sourceType || 'farm',
                destination_type: 'warehouse',
                notes: data.notes || null
            };
            const response = await apiPost('/api/warehouses/transactions', payload);
            console.log('Incoming produce recorded successfully:', response);
            await fetchAllData(); // Refresh all data
        } catch (error) {
            console.error('Error recording incoming produce:', error);
            let errorMessage = 'Failed to record incoming produce';
            if (error instanceof Error) {
                errorMessage = error.message;
            } else if (typeof error === 'object' && error !== null) {
                const err = error as any;
                if (typeof err.response?.data?.error === 'string') {
                    errorMessage = err.response.data.error as string;
                } else if (typeof err.message === 'string') {
                    errorMessage = err.message;
                } else if (typeof err.error === 'string') {
                    errorMessage = err.error;
                }
            }
            alert(errorMessage);
            throw error;
        }
    };

    const handleRecordOutgoing = async (data: any) => {
        try {
            console.log('Recording outgoing shipment:', data);
            const payload = {
                facility_id: Number(data.facilityId),
                crop_type: data.cropType,
                item_type: data.itemType || 'produce',
                quantity_kg: Number(data.quantityKg),
                direction: 'outgoing',
                reason: data.reason || 'sale',
                related_farmer_id: data.farmerId ? Number(data.farmerId) : null,
                related_cohort_id: data.cohortId ? Number(data.cohortId) : null,
                destination_type: data.destinationType || 'buyer',
                destination_id: data.destinationId ? Number(data.destinationId) : null,
                quality_grade: data.qualityGrade || null,
                temperature_at_transaction: data.temperature ? Number(data.temperature) : null,
                humidity_at_transaction: data.humidity ? Number(data.humidity) : null,
                expiry_date: data.expiryDate || null,
                batch_reference: data.batchReference || null,
                notes: data.notes || null
            };
            const response = await apiPost('/api/warehouses/transactions', payload);
            console.log('Outgoing shipment recorded successfully:', response);
            await fetchAllData(); // Refresh all data
        } catch (error) {
            console.error('Error recording outgoing shipment:', error);
            let errorMessage = 'Failed to record outgoing shipment';
            if (error instanceof Error) {
                errorMessage = error.message;
            } else if (typeof error === 'object' && error !== null) {
                const err = error as any;
                if (typeof err.response?.data?.error === 'string') {
                    errorMessage = err.response.data.error as string;
                } else if (typeof err.message === 'string') {
                    errorMessage = err.message;
                } else if (typeof err.error === 'string') {
                    errorMessage = err.error;
                }
            }
            alert(errorMessage);
            throw error;
        }
    };

    const handleRecordInternal = async (data: any) => {
        try {
            const payload = {
                facility_id: Number(data.facilityId),
                crop_type: data.cropType,
                item_type: data.itemType || 'compost',
                quantity_kg: Number(data.quantityKg),
                direction: 'internal',
                reason: data.reason || 'transfer',
                source_type: data.sourceType || 'warehouse',
                source_id: data.sourceId ? Number(data.sourceId) : null,
                destination_type: data.destinationType || 'distribution_point',
                destination_id: data.destinationId ? Number(data.destinationId) : null,
                related_farmer_id: data.farmerId ? Number(data.farmerId) : null,
                related_cohort_id: data.cohortId ? Number(data.cohortId) : null,
                temperature_at_transaction: data.temperature ? Number(data.temperature) : null,
                humidity_at_transaction: data.humidity ? Number(data.humidity) : null,
                batch_reference: data.batchReference || null,
                notes: data.notes || null
            };
            await apiPost('/api/warehouses/transactions', payload);
            await fetchAllData();
        } catch (error) {
            console.error('Error recording internal movement:', error);
            alert(error instanceof Error ? error.message : 'Failed to record internal movement');
            throw error;
        }
    };

    const handleCreateFacility = async (data: any) => {
        try {
            console.log('Creating facility with data:', data);
            const response = await apiPost('/api/warehouses/facilities', data);
            console.log('Facility created successfully:', response);
            await fetchAllData(); // Refresh all data
            setFacilityModalOpen(false);
        } catch (error) {
            console.error('Error creating facility:', error);
            let errorMessage = 'Failed to create facility';
            if (error instanceof Error) {
                errorMessage = error.message;
            } else if (typeof error === 'object' && error !== null) {
                const err = error as any;
                if (typeof err.response?.data?.error === 'string') {
                    errorMessage = err.response.data.error as string;
                } else if (typeof err.message === 'string') {
                    errorMessage = err.message;
                } else if (typeof err.error === 'string') {
                    errorMessage = err.error;
                }
            }
            alert(errorMessage);
            throw error;
        }
    };

    const handleUpdateFacility = async (data: any) => {
        try {
            if (!selectedFacility?.id) return;
            console.log('Updating facility with data:', data);
            const response = await apiPut(`/api/warehouses/facilities/${selectedFacility.id}`, data);
            console.log('Facility updated successfully:', response);
            await fetchAllData(); // Refresh all data
            setFacilityModalOpen(false);
            setIsEditing(false);
            setSelectedFacility(null);
        } catch (error) {
            console.error('Error updating facility:', error);
            let errorMessage = 'Failed to update facility';
            if (error instanceof Error) {
                errorMessage = error.message;
            } else if (typeof error === 'object' && error !== null) {
                const err = error as any;
                if (typeof err.response?.data?.error === 'string') {
                    errorMessage = err.response.data.error as string;
                } else if (typeof err.message === 'string') {
                    errorMessage = err.message;
                } else if (typeof err.error === 'string') {
                    errorMessage = err.error;
                }
            }
            alert(errorMessage);
            throw error;
        }
    };

    const handleFacilityClick = (facility: any) => {
        setSelectedFacility(facility);
        setDetailsModalOpen(true);
    };

    const handleEditFacility = (facility: any) => {
        setSelectedFacility(facility);
        setIsEditing(true);
        setDetailsModalOpen(false);
        setFacilityModalOpen(true);
    };

    const handleOpenCreateModal = () => {
        setSelectedFacility(null);
        setIsEditing(false);
        setFacilityModalOpen(true);
    };

    const handleExportReport = () => {
        // Convert transactions to CSV
        const headers = ['Date', 'Crop', 'Quantity (kg)', 'Direction', 'Reason', 'Status'];
        const csvData = transactions.map(t => [
            new Date(t.created_at).toLocaleDateString(),
            t.crop_type,
            t.quantity_kg,
            t.direction,
            t.reason,
            t.status || 'recorded'
        ]);

        const csvContent = [
            headers.join(','),
            ...csvData.map(row => row.join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `warehouse-report-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00A1DE]"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#007AA7] via-[#00A1DE] to-cyan-600 shadow-xl">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-7">
                    <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 items-start">
                        <div className="xl:col-span-5">
                            <h1 className="text-3xl md:text-4xl font-bold text-white leading-tight">
                                Warehouse Operations Control
                            </h1>
                            <p className="text-cyan-100 text-lg mt-3">
                                {facilities.length} Facilities • {stats ? `${stats.totalStored} kg in stock` : 'Loading...'} • {inventorySnapshot.length} tracked stock lines
                            </p>
                        </div>

                        {/* Quick Actions */}
                        <div className="xl:col-span-7">
                            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                            <button
                                onClick={handleOpenCreateModal}
                                className="inline-flex items-center justify-center px-4 py-3 bg-white text-[#00A1DE] rounded-lg hover:bg-blue-50 transition-all font-semibold shadow-lg"
                            >
                                <BuildingStorefrontIcon className="h-5 w-5 mr-2" />
                                Create Facility
                            </button>
                            <button
                                onClick={() => setIncomingModalOpen(true)}
                                className="inline-flex items-center justify-center px-4 py-3 bg-[#FFD700] text-gray-900 rounded-lg hover:bg-yellow-400 transition-all font-semibold shadow-lg"
                            >
                                <PlusIcon className="h-5 w-5 mr-2" />
                                Record Incoming
                            </button>
                            <button
                                onClick={() => setOutgoingModalOpen(true)}
                                className="inline-flex items-center justify-center px-4 py-3 bg-[#FFD700] text-gray-900 rounded-lg hover:bg-yellow-400 transition-all font-semibold shadow-lg"
                            >
                                <PlusIcon className="h-5 w-5 mr-2" />
                                Record Outgoing
                            </button>
                            <button
                                onClick={() => setInternalModalOpen(true)}
                                className="inline-flex items-center justify-center px-4 py-3 bg-white text-indigo-700 rounded-lg hover:bg-indigo-50 transition-all font-semibold shadow-lg"
                            >
                                <ArrowsRightLeftIcon className="h-5 w-5 mr-2" />
                                Internal Movement
                            </button>
                            <button
                                onClick={handleExportReport}
                                className="inline-flex items-center justify-center px-4 py-3 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-all font-semibold shadow-lg sm:col-span-2 xl:col-span-1"
                            >
                                <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
                                Export Report
                            </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8 relative z-10">
                <section className="grid grid-cols-1 xl:grid-cols-12 gap-6 items-start">
                    <div className="xl:col-span-8">
                        <WarehouseMapDashboard
                            facilities={facilities}
                            onFacilityClick={handleFacilityClick}
                        />
                    </div>
                    <div className="xl:col-span-4">
                        <StockWorkflowBoard transactions={transactions} />
                    </div>
                </section>

                {/* KPI Dashboard */}
                <section>
                    <InventoryDashboard stats={stats} loading={!stats} />
                </section>

                {/* Facilities Table */}
                <section>
                    <WarehouseFacilitiesTable
                        facilities={facilities}
                        onViewDetails={handleFacilityClick}
                        onScheduleMaintenance={(facility) => console.log('Schedule maintenance:', facility)}
                    />
                </section>

                {/* Transactions Table */}
                <section>
                    <InventoryTransactionsTable
                        transactions={transactions}
                        onViewDetails={(transaction) => {
                            setSelectedTransaction(transaction);
                            setTransactionModalOpen(true);
                        }}
                    />
                </section>

                <section>
                    <TraceabilityPanel rows={traceability} loading={loading} />
                </section>
            </div>

            {/* Modals */}
            <FacilityCreationModal
                isOpen={facilityModalOpen}
                onClose={() => setFacilityModalOpen(false)}
                onSubmit={isEditing ? handleUpdateFacility : handleCreateFacility}
                initialData={isEditing ? selectedFacility : undefined}
                isEditing={isEditing}
            />

            <RecordIncomingModal
                isOpen={incomingModalOpen}
                onClose={() => setIncomingModalOpen(false)}
                onSubmit={handleRecordIncoming}
                facilities={facilities}
            />

            <RecordOutgoingModal
                isOpen={outgoingModalOpen}
                onClose={() => setOutgoingModalOpen(false)}
                onSubmit={handleRecordOutgoing}
                facilities={facilities}
            />

            <RecordInternalMovementModal
                isOpen={internalModalOpen}
                onClose={() => setInternalModalOpen(false)}
                onSubmit={handleRecordInternal}
                facilities={facilities}
            />

            <FacilityDetailsModal
                facility={selectedFacility}
                isOpen={detailsModalOpen}
                onClose={() => setDetailsModalOpen(false)}
                onEdit={handleEditFacility}
            />

            <TransactionDetailsModal
                transaction={selectedTransaction}
                isOpen={transactionModalOpen}
                onClose={() => setTransactionModalOpen(false)}
            />
        </div>
    );
};

export default WarehouseView;
